import { useContext, useEffect } from 'react';
import Image from 'next/future/image';
import Link from 'next/link';
import { CartContext } from 'pages/cart';
import {
  RC_DURATION,
  REEBELO_CONDITIONS,
  REEBELO_US_SERVICE_PROVIDERS,
  ReebeloConditions,
  TagHelpers,
} from '@lambda/reebelo';
import {
  Cart,
  CartLine,
  ProductVariant,
} from '@lambda/commons/apis/shopify/types/storefront';
import classNames from 'classnames';
import { convertCellularFieldsToServiceProvider } from '@lambda/business/offers/cellular';
import { AppCtx } from 'pages/_app';
import { extractTags } from '@lambda/reebelo/src/tagHelpers';
import { isEmpty } from 'lodash-es';
import i18n from '@/settings/i18n';
import settings, { priceFormater } from '@/settings';
import { formatOfferUri } from '@/lib/offer';
import useComparePrices from '@/lib/use-pim-compare-price';
import {
  encodeDecodeForAttr,
  UpdateCartParams,
  CartItemAttribute,
} from '@/lib/use-cart';
import { CartLineWithRCareSpecs } from '@/components/cart/types';
import rCareIcon from '@/public/icons/cart/rCareNew.svg';
import infoIconBlack from '@/public/icons/product/infoIconBlack.svg';
import batteryHealthIcon from '@/public/icons/cart/batteryHealthIcon.svg';
import NumberInput, { NumberInputValue } from '../forms/NumberInput';

import {
  maxQuantityPerOrder,
  updateParentItemDeliveryAttributes,
} from '@/lib/order';
import useDeliveryDate, {
  formatDeliveryDateRange,
} from '@/lib/use-delivery-date';
import { useVendorDispatch } from '@/lib/use-vendor-details';
import {
  trackAddToCart,
  trackReebeloCareRemoved,
  trackRemoveFromCart,
} from '@/lib/analytics/trackProduct';
import { logger } from '@/lib/logger';
import {
  getBatteryHealthLabel,
  getCarrierImage,
} from '@/lib/collections/helpers';
import {
  ProductEventProperties,
  ReebeloCareTrackRequest,
} from '@/lib/analytics/types';
import { logOutOfStockCartAddition, createOutOfStockLogData } from '@/lib/cart-out-of-stock-logger';

const t = i18n.init();

const handleRemoveButtonClick =
  ({
    merch,
    lineId,
    remove,
    update,
    parentItem,
    selectedBundle,
    standardMinDispatch,
    standardMaxDispatch,
    standardMinDeliverBy,
    standardMaxDeliverBy,
    standardCutoffTime,
    standardCutoffTimezone,
    reebeloCareDuration,
  }: {
    lineId: string;
    merch: ProductVariant;
    comparePrice?: number;
    selectedBundle?: Array<string>;
    parentItem?: CartLineWithRCareSpecs;
    standardMinDispatch: number;
    standardMaxDispatch: number;
    standardMinDeliverBy: string;
    standardMaxDeliverBy: string;
    standardCutoffTime: string;
    standardCutoffTimezone: string;
    remove: (lineIds: string[]) => Promise<void>;
    update: (lines: UpdateCartParams, cartIdParam?: string) => Promise<void>;
    isPostPurchaseRc: boolean;
    reebeloCareDuration: RC_DURATION;
  }) =>
  async () => {
    const lines = [];
    const { product } = merch;
    const tags = TagHelpers.extractTags(product.tags);
    const isExpressShippingItem = product.title
      .toLowerCase()
      .includes('express shipping');

    const isReebeloCareItem = product.title
      .toLowerCase()
      .includes('reebelocare');

    if (isExpressShippingItem) {
      if (parentItem == null) {
        logger.error('express shipping item without parent item');
      } else {
        // update parent delivery attribute if express item is removed
        await updateParentItemDeliveryAttributes({
          parentItem,
          update,
          attributes: new Map<string, string>([
            ['_deliver_min', standardMinDeliverBy],
            ['_deliver_max', standardMaxDeliverBy],
            ['_dispatch_min', standardMinDispatch.toString()],
            ['_dispatch_max', standardMaxDispatch.toString()],
            ['_cutoff_time', standardCutoffTime],
            ['_cutoff_timezone', standardCutoffTimezone],
          ]),
        });
      }
    }

    if (selectedBundle) lines.push(...selectedBundle);

    lines.push(lineId);

    if (isReebeloCareItem && parentItem) {
      const variantId = merch.id.split('/').pop();
      const { psku } = extractTags(parentItem.merchandise.product.tags);

      const trackingProps: ReebeloCareTrackRequest = {
        entry_point: 'Cart',
        reebelocare_id: Number(variantId ?? 0),
        device_psku: psku ?? '',
        device_vendor: parentItem?.merchandise.product.vendor ?? '',
        device_sku: parentItem?.merchandise.sku ?? '',
        duration: reebeloCareDuration,
      };

      trackReebeloCareRemoved(trackingProps);
    } else {
      trackRemoveFromCart({
        variant: merch,
        properties: {
          product_id: product.id,
          psku: tags.psku,
          vendor: product.vendor,
        },
      });
    }

    return remove(lines);
  };

const getMaxQuantityPerOrder = (merch: ProductVariant, psku?: string) => {
  if (!psku) return merch.quantityAvailable;

  const pskuMax = maxQuantityPerOrder[psku];

  if (pskuMax) return Math.min(pskuMax, merch.quantityAvailable);

  return merch.quantityAvailable;
};

const handleQtyChange =
  ({
    merch,
    lineId,
    update,
    selectedBundle,
    availableQty,
    lineItem,
    currentQty,
    psku,
  }: {
    lineId: string;
    selectedBundle?: Array<CartLine>;
    availableQty: number;
    comparePrice?: number;
    merch: ProductVariant;
    update: (lines: UpdateCartParams, cartIdParam?: string) => Promise<void>;
    lineItem: CartLineWithRCareSpecs;
    currentQty: number;
    psku?: string;
  }): ((value: NumberInputValue) => void) =>
  (value: NumberInputValue) => {
    const maxQuantity = getMaxQuantityPerOrder(merch, psku);

    if (currentQty > maxQuantity) return;
    const isGiftCard = merch.product.handle === 'reebelos-gift-card';
    let newValue = Number(value) > Number(availableQty) ? availableQty : value;

    // Log if user tried to exceed available stock
    if (Number(value) > Number(availableQty)) {
      const productData = {
        sku: merch.sku,
        psku: psku || 'unknown',
        title: merch.product.title,
        vendor: merch.product.vendor,
        price: merch.priceV2?.amount || 0,
        condition: undefined,
        color: undefined,
        brand: undefined,
        storage: undefined,
        category: undefined,
      };

      const cartData = {
        quantity: Number(value),
        cartId: undefined,
        cartSize: undefined,
        itemPosition: undefined,
        accessoryModalSource: false,
      };

      const stockData = {
        availableStock: availableQty,
        requestedQuantity: Number(value),
      };

      const logData = createOutOfStockLogData(productData, cartData, stockData);
      logOutOfStockCartAddition(logData);

      console.warn('User attempted to increase quantity beyond available stock in cart item', {
        lineId,
        availableQty,
        requestedQty: Number(value),
        product: {
          title: merch.product.title,
          vendor: merch.product.vendor,
          sku: merch.sku,
        },
      });
    }

    if (!isGiftCard && Number(newValue) < 0) return;
    if (isGiftCard) newValue = value;

    const normalizedFieldValue = value === 0 ? '' : newValue;

    if (
      typeof normalizedFieldValue === 'number' &&
      typeof newValue === 'number'
    ) {
      const updates = [
        {
          id: lineId,
          quantity: newValue,
          attributes: lineItem.attributes as Array<CartItemAttribute>,
        },
      ];

      if (selectedBundle && selectedBundle.length > 0) {
        selectedBundle.forEach((bundleItem) => {
          const updateItem = {
            id: bundleItem.id,
            quantity: newValue as number,
            attributes: bundleItem.attributes as Array<CartItemAttribute>,
          };

          updates.push(updateItem);
        });
      }

      update(updates);

      if (merch) {
        const tags = TagHelpers.extractTags(merch.product.tags);
        const { product } = merch;
        const itemInStock = Number(value) > currentQty;
        const properties: ProductEventProperties = {
          product_id: product.id,
          psku: tags.psku,
          vendor: product.vendor,
        };

        if (itemInStock) {
          trackAddToCart({
            variant: merch,
            properties,
          });
        } else {
          trackRemoveFromCart({
            variant: merch,
            properties,
          });
        }
      }
    }
  };

export type UpgradedGuidePopup = { open: boolean; index: number };

export default function CartItem(props: {
  cart: Cart;
  index: number;
  lineItemId: string;
  isItemsVisible: boolean;
  isUpgradeGuideOpen: UpgradedGuidePopup;
  lineItem: CartLineWithRCareSpecs;
  setComparePrices: (price: number) => void;
  handleUpgradedOpen: (data: UpgradedGuidePopup) => void;
}) {
  const { lineItem, cart, index, isUpgradeGuideOpen, handleUpgradedOpen } =
    props;
  const CartHelpers = useContext(CartContext);
  const { loading, remove, update } = CartHelpers;
  const lineItems = cart?.lines.edges.map((e) => e.node) || [];
  const { merchandise: merch, id: lineId, quantity } = lineItem;

  const upgrade = lineItem.attributes.find((attribute) =>
    attribute.key.includes(t`Free upgrade from`),
  );

  const variant = TagHelpers.extractTags(merch.product.tags);
  const isGiftCard = merch.product.handle === 'reebelos-gift-card';
  const variantId = merch.id.split('/').pop();
  const link = isGiftCard
    ? `products/reebelos-gift-card?variant=${variantId}`
    : formatOfferUri(merch.product.handle, merch.product.tags);
  const { title } = merch.product;

  const isWarrantyItem = title.toLowerCase().includes('reebelocare');
  const isExpressShippingItem = title
    .toLowerCase()
    .includes('express shipping');
  const parentItem =
    isWarrantyItem || isExpressShippingItem
      ? lineItems.find(
          (item) =>
            encodeDecodeForAttr.decode(lineItem.attributes) ===
            item.merchandise.sku,
        )
      : undefined;

  const batteryHealth = lineItem.attributes.find(
    (attribute) => attribute.key === '_battery_health',
  )?.value;

  // get parent item delivery date and dispatch if available
  const { zipcodeStateData } = useContext(AppCtx);

  const parentDeliveryDate = useDeliveryDate({
    zipcode: zipcodeStateData?.zipcode,
    state: zipcodeStateData?.state,
    category:
      extractTags(parentItem?.merchandise.product.tags || []).category || '',
    vendor: parentItem?.merchandise.product.vendor || '',
  });
  const parentDispatch = useVendorDispatch(
    parentItem?.merchandise.product.vendor || '',
  );

  const expressShippingMinDate = parentItem?.attributes.find(
    (attr) => attr.key === '_deliver_min',
  )?.value;
  const expressShippingMaxDate = parentItem?.attributes.find(
    (attr) => attr.key === '_deliver_max',
  )?.value;
  const { psku } = variant;
  const v2Price = Number(merch.priceV2.amount);
  const finalPrice = TagHelpers.calculatePriceAfterSubsidy(
    v2Price,
    variant.subsidy,
  );
  const price = isWarrantyItem ? lineItem.rCarePrice || finalPrice : finalPrice;
  const bundle = lineItems.filter(
    (item) => encodeDecodeForAttr.decode(item.attributes) === merch.sku,
  );
  const comparePrice = useComparePrices({
    psku,
    storage: variant.storage,
    tags: merch.product.tags,
    actualPrice: price,
  });

  const [itemName, ...specs] = title
    .split(' - ')
    .filter((i) => !REEBELO_US_SERVICE_PROVIDERS.includes(i.split('/')[0]));

  let itemSpecs = isGiftCard
    ? priceFormater.short(price)
    : specs
        .filter(
          (s) =>
            !['fully unlocked', 'gsm unlocked', 'gsm locked'].includes(
              s.toLowerCase(),
            ),
        )
        .map((s) =>
          s
            .replace('Very Good', 'Good')
            .concat(
              REEBELO_CONDITIONS(settings.store).includes(
                s as ReebeloConditions,
              )
                ? ' Condition'
                : '',
            ),
        )
        .join(' | ')
        .trim();

  const isUSStore = ['reebelo-us', 'reebelo-dev'].includes(settings.store);

  const itemImage = isWarrantyItem ? rCareIcon : merch.image?.url ?? '';

  // ReebeloCare & Express Shipping
  if (
    (isWarrantyItem &&
      !lineItem.attributes.some((attr) => attr.key === 'PREVIOUS_ORDER_ID')) ||
    isExpressShippingItem
  )
    itemSpecs = `for ${lineItem.rCareSpecs} (x${quantity})`;

  // Post Purchase ReebeloCare
  let itemForPreviousOrder = '';

  if (
    isWarrantyItem &&
    lineItem.attributes.some((attr) => attr.key === 'PREVIOUS_ORDER_ID')
  ) {
    const rcFor = lineItem.attributes
      .find((attr) => attr.key === 'For')
      ?.value.split('] ')[1]
      .split('-')[0]
      .trim();

    const rcPrevOrder = lineItem.attributes.find(
      (attr) => attr.key === 'PREVIOUS_ORDER_ID',
    )?.value;

    itemSpecs = `for ${rcFor} (x${quantity})`;
    itemForPreviousOrder = `Previous Order: #${rcPrevOrder}`;
  }

  useEffect(() => {
    props.setComparePrices(comparePrice || price);
  }, [comparePrice]);

  if (!props.isItemsVisible) return <div />;

  const upgradedCondition =
    upgrade && upgrade.value?.split('to')[1]?.trim().toLowerCase().toString();
  const serviceProvider = convertCellularFieldsToServiceProvider(variant);

  const isUnlocked = serviceProvider.includes('Unlocked');

  const isAdditionalMonthsEnabled = [
    'reebelo-ca',
    'reebelo-us',
    'reebelo-dev',
  ].includes(settings.store);

  const getItemTitle = () => {
    if (isWarrantyItem) {
      if (isAdditionalMonthsEnabled) {
        const duration =
          lineItem.attributes.find((att) => att.key === 'Duration')?.value ??
          RC_DURATION['24_MONTH'];

        return `${duration}-Months' ReebeloCare Protection`;
      }

      return t`2 Years Extended Warranty`;
    }
    if (isExpressShippingItem) return t`Express Shipping`;

    if (isUSStore) {
      if (!isEmpty(serviceProvider)) {
        const provider = isUnlocked
          ? 'Unlocked'
          : `${serviceProvider.join('/')} - Locked`;

        return `${itemName} - ${provider}`;
      }
    }

    return itemName;
  };
  const itemTitle = getItemTitle();

  const isClickableItem = !isExpressShippingItem && !isWarrantyItem;

  return (
    <div
      className="group w-full border-b p-4 pl-0 pr-0 last:mb-0 xl:p-5 xl:pl-5 xl:pr-5"
      id={`e2e-cart-line-item-${index}`}
    >
      <div className="flex w-full flex-wrap items-center justify-between">
        <div className="flex w-full items-start">
          <span className="mx-0 w-[60px] xs:w-[65px] lg:mx-1">
            {merch.image && (
              <Image
                width={60}
                height={60}
                alt={title}
                src={itemImage}
                className="h-[60px] w-[60px] object-contain"
              />
            )}
          </span>
          <div className="flex w-[calc(100%-60px)] flex-wrap xs:w-[calc(100%-70px)]">
            <div
              className="w-3/5 px-1 xxs:w-2/3 xs:w-3/4 sm:px-2"
              id={`e2e-cart-line-item-details-${index}`}
            >
              <h5
                // eslint-disable-next-line tailwindcss/no-custom-classname
                className="two-line-ellipsis mb-[3px] text-sm font-bold !leading-tight text-gray-700"
              >
                {isClickableItem ? (
                  <Link href={link}>
                    <a target="_blank">{itemTitle}</a>
                  </Link>
                ) : (
                  <span>{itemTitle}</span>
                )}
              </h5>
              <p className="text-[13px] text-gray-700">{itemSpecs}</p>
              {isExpressShippingItem && (
                <p className="text-[13px] text-gray-700">
                  by{' '}
                  {formatDeliveryDateRange(
                    {
                      minDeliveryDate: expressShippingMinDate || '',
                      maxDeliveryDate: expressShippingMaxDate || '',
                    },
                    'short',
                  )}
                </p>
              )}
              {itemForPreviousOrder && (
                <p className="mt-1 text-[13px] text-gray-700">
                  {itemForPreviousOrder}
                </p>
              )}
              {upgrade?.value &&
                !isGiftCard &&
                variant.condition !== upgrade?.value && (
                  <>
                    <div className="relative pb-1 text-[13px] leading-4 text-gray-700">
                      {`${upgrade.key} ${upgrade.value}`}
                      <span
                        className="mt-1 ml-1 inline-block h-[12px] w-[12px] cursor-pointer"
                        onMouseEnter={() => {
                          handleUpgradedOpen({ open: true, index });
                        }}
                        onMouseLeave={() => {
                          handleUpgradedOpen({ open: false, index });
                        }}
                      >
                        <Image
                          src={infoIconBlack}
                          alt="free upgrade"
                          className="object-contain"
                        />
                      </span>
                      {isUpgradeGuideOpen.index === index &&
                        isUpgradeGuideOpen.open &&
                        upgradedCondition && (
                          <div className="absolute top-[65%] right-0 z-40 mt-2 w-full rounded-md text-gray-700 shadow-lg xs:top-[full]">
                            <div className="bg-white px-3 py-3 text-left text-[12px] leading-tight xs:px-4 xs:py-3">{t`We’ve upgraded you to a phone in ${{
                              upgradedCondition,
                            }} condition for the same price!`}</div>
                          </div>
                        )}
                    </div>
                  </>
                )}
              {!!batteryHealth && (
                <div className="mt-1 mb-2 w-fit rounded-2xl border bg-gray-100 px-2">
                  <Image
                    src={batteryHealthIcon}
                    alt="battery health"
                    className="mr-1 inline-block"
                    priority
                    width={7}
                    height={7}
                  />
                  <span className="text-xs">
                    Battery: {getBatteryHealthLabel(batteryHealth)}
                  </span>
                </div>
              )}
              {serviceProvider.length > 0 && (
                <>
                  <p className="mt-1 text-[13px] text-gray-700">
                    {t`Compatible with`}
                  </p>
                  <div className="mb-1 flex">
                    {serviceProvider
                      .filter((s) => s !== 'Unlocked' && s !== 'Sprint')
                      .map((provider) => {
                        const providerImage = getCarrierImage(provider)?.image;

                        if (!providerImage) return null;

                        return (
                          <div
                            key={`${index}-${provider}`}
                            className="mt-[3px] mr-1 flex w-[50px] items-center justify-center rounded-md border border-gray-200 px-[5px] py-1 xxs:mr-2 xxs:w-14 xs:w-16 xs:px-2"
                          >
                            <div className="relative w-full">
                              <Image
                                quality={100}
                                alt={provider}
                                src={providerImage}
                              />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </>
              )}
            </div>
            <div className="relative mb-auto flex w-2/5 flex-wrap justify-end xxs:w-1/3 xs:w-1/4">
              <p
                className="w-full text-right text-sm font-bold text-gray-700"
                id={`e2e-cart-line-${props.index}-total`}
              >
                {priceFormater.long(price)}
              </p>
            </div>
            <div className="relative flex w-full flex-wrap items-center justify-end">
              <span
                onClick={handleRemoveButtonClick({
                  merch,
                  lineId,
                  remove,
                  update,
                  comparePrice,
                  selectedBundle: bundle.map((item) => item.id),
                  parentItem,
                  standardMinDispatch: parentDispatch.min,
                  standardMaxDispatch: parentDispatch.max,
                  standardMinDeliverBy:
                    parentDeliveryDate?.standard.minDeliveryDate || '',
                  standardMaxDeliverBy:
                    parentDeliveryDate?.standard.maxDeliveryDate || '',
                  standardCutoffTime:
                    parentDeliveryDate?.standard.cutoffTime || '',
                  standardCutoffTimezone:
                    parentDeliveryDate?.standard.cutoffTimezone || '',
                  isPostPurchaseRc: itemForPreviousOrder.length > 0,
                  reebeloCareDuration: (lineItem.attributes.find(
                    (att) => att.key === 'Duration',
                  )?.value ?? RC_DURATION['24_MONTH']) as RC_DURATION,
                })}
                className={classNames(
                  'mr-4 cursor-pointer pl-3 text-right text-xs text-gray-700 underline underline-offset-2 hover:text-blue-500 md:block lg:pl-0',
                  { '!mr-0': isWarrantyItem || isExpressShippingItem },
                )}
              >
                {t`Remove`}
              </span>
              {!isWarrantyItem && !isExpressShippingItem && (
                <div className="flex">
                  <NumberInput
                    min={1}
                    value={quantity}
                    loading={loading}
                    index={props.index}
                    className="mb-2 flex"
                    max={getMaxQuantityPerOrder(merch, psku)}
                    onChange={handleQtyChange({
                      merch,
                      lineId,
                      update,
                      selectedBundle: bundle,
                      comparePrice,
                      availableQty: merch.quantityAvailable,
                      lineItem: props.lineItem,
                      currentQty: quantity,
                    })}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
